import { ReactNode, useMemo, useState } from "react";
import { Building2, CalendarDays, Clapperboard, Clock, Coins, Info, Sparkles } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";
import { Modal, ModalBody, ModalHeader } from "components/common/modal";
import { BudgetI, TaxIncentive } from "./types";
import { formatBEDateToDateTimeString } from "utils";
import { CURRENCIES, ProductionState } from "config";
import { TaxIncentivesModal } from "./TaxIncentivesModal";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useVersionSettings } from "hooks/useProjects/useVersions";
import { parseVersionProductionLocation } from "utils/budgetAssumptionsMapping";
import { resolveBudgetDisplayLocation } from "utils/resolveBudgetDisplayLocation";
import { BUDGET_PANEL_CARD_CLASS, BUDGET_PANEL_SURFACE_CLASS } from "./BudgetGridCommon";

interface BudgetDialogProps {
  budgetDetail?: Omit<BudgetI, "categories">;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
}

function readProductionStateFromVersionPayload(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  if (typeof record.production_state === "string") return record.production_state;
  const settings = record.settings;
  if (settings && typeof settings === "object" && !Array.isArray(settings)) {
    const ps = (settings as Record<string, unknown>).production_state;
    if (typeof ps === "string") return ps;
  }
  return null;
}

function readProductionLocationFromVersionPayload(data: unknown) {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  if (record.production_location) {
    return parseVersionProductionLocation(record.production_location);
  }
  const settings = record.settings;
  if (settings && typeof settings === "object" && !Array.isArray(settings)) {
    return parseVersionProductionLocation(
      (settings as Record<string, unknown>).production_location
    );
  }
  return null;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
      {children}
    </span>
  );
}

interface DetailRow {
  id: string;
  label: string;
  value?: string | number | null;
  icon: ReactNode;
}

function DetailPanel({ rows }: { rows: DetailRow[] }) {
  const visible = rows.filter(
    (row) => row.value !== undefined && row.value !== null && row.value !== ""
  );
  if (visible.length === 0) return null;

  return (
    <div className={BUDGET_PANEL_SURFACE_CLASS}>
      {visible.map((row, index) => (
        <div
          key={row.id}
          className={`flex items-start gap-3 px-4 py-3.5 ${
            index > 0 ? "border-t border-border" : ""
          }`}
        >
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/50 text-muted-foreground [&>svg]:h-[15px] [&>svg]:w-[15px]">
            {row.icon}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
            <SectionLabel>{row.label}</SectionLabel>
            <span className="text-sm font-medium leading-snug text-foreground/90">{row.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

interface AssumptionRow {
  id: string;
  label: string;
  value: ReactNode;
}

function AssumptionPanel({ rows }: { rows: AssumptionRow[] }) {
  if (rows.length === 0) return null;

  return (
    <div className={BUDGET_PANEL_SURFACE_CLASS}>
      {rows.map((row, index) => (
        <div
          key={row.id}
          className={`flex flex-col gap-1 px-4 py-3.5 text-left ${
            index > 0 ? "border-t border-border" : ""
          }`}
        >
          <SectionLabel>{row.label}</SectionLabel>
          <div className="text-sm leading-snug text-foreground">{row.value}</div>
        </div>
      ))}
    </div>
  );
}

function AssumptionsEmptyState() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-4 text-left">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Sparkles className="h-4 w-4" />
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-medium text-foreground/80">No assumptions yet</span>
        <span className="text-sm leading-relaxed text-muted-foreground">
          Open Rebudget to set production base, tax incentives, and other rebudget inputs.
        </span>
      </div>
    </div>
  );
}

function useBudgetSummaryData(budgetDetail?: Omit<BudgetI, "categories">) {
  const project = useSelector((state: AppState) => state.project);
  const { projectId, documentId } = useProjectIdentifiers();
  const versionSettingsQuery = useVersionSettings(projectId, documentId ?? undefined);

  const displayLocation = useMemo(
    () =>
      resolveBudgetDisplayLocation({
        rerunParams: budgetDetail?.rerun_params,
        assumptions: budgetDetail?.assumptions,
        versionProductionState: readProductionStateFromVersionPayload(versionSettingsQuery.data),
        versionProductionLocation: readProductionLocationFromVersionPayload(
          versionSettingsQuery.data
        ),
        projectProductionState: project.features?.production_state,
      }),
    [
      budgetDetail?.assumptions,
      budgetDetail?.rerun_params,
      project.features?.production_state,
      versionSettingsQuery.data,
    ]
  );

  const showProductionBase =
    Boolean(budgetDetail) &&
    Boolean(displayLocation.productionBaseLabel) &&
    !budgetDetail?.tax_incentives?.length;

  const showTaxIncentiveLink = Boolean(displayLocation.taxState);

  return { displayLocation, showProductionBase, showTaxIncentiveLink };
}

function BudgetDetailsContent({ budgetDetail }: { budgetDetail?: Omit<BudgetI, "categories"> }) {
  const currencyLabel = budgetDetail?.currency
    ? (CURRENCIES.find((cur) => cur.abbreviation === budgetDetail.currency.toUpperCase())
        ?.abbreviation ?? budgetDetail.currency)
    : undefined;

  const detailRows: DetailRow[] = [
    {
      id: "title",
      label: "Title",
      value: budgetDetail?.document?.project?.name,
      icon: <Clapperboard />,
    },
    {
      id: "company",
      label: "Production company",
      value: budgetDetail?.document?.project?.workspace?.name,
      icon: <Building2 />,
    },
    {
      id: "shoot-days",
      label: "Estimated shoot days",
      value: budgetDetail?.estimated_shoot_days,
      icon: <CalendarDays />,
    },
    {
      id: "currency",
      label: "Currency",
      value: currencyLabel,
      icon: <Coins />,
    },
    {
      id: "created",
      label: "Created",
      value: budgetDetail?.created_at
        ? formatBEDateToDateTimeString(budgetDetail.created_at)
        : undefined,
      icon: <Clock />,
    },
    {
      id: "updated",
      label: "Last updated",
      value: budgetDetail?.updated_at
        ? formatBEDateToDateTimeString(budgetDetail.updated_at)
        : undefined,
      icon: <Clock />,
    },
  ];

  return (
    <div className="flex flex-col gap-2.5">
      <SectionLabel>Overview</SectionLabel>
      <DetailPanel rows={detailRows} />
    </div>
  );
}

function BudgetAssumptionsContent({
  budgetDetail,
  displayLocation,
  showProductionBase,
  showTaxIncentiveLink,
  onProductionStateClick,
  onTaxIncentiveClick,
}: {
  budgetDetail?: Omit<BudgetI, "categories">;
  displayLocation: ReturnType<typeof resolveBudgetDisplayLocation>;
  showProductionBase: boolean;
  showTaxIncentiveLink: boolean;
  onProductionStateClick: (state: ProductionState) => void;
  onTaxIncentiveClick: (incentive: TaxIncentive) => void;
}) {
  const visibleAssumptions = (budgetDetail?.assumptions ?? []).filter(
    (assume) => assume.name?.toUpperCase() !== "LOCATION"
  );
  const hasTaxIncentives = Boolean(budgetDetail?.tax_incentives?.length);

  const assumptionRows: AssumptionRow[] = [];

  if (showProductionBase) {
    assumptionRows.push({
      id: "production-base",
      label: "Production base",
      value: (
        <>
          {displayLocation.productionBaseLabel}
          {showTaxIncentiveLink && displayLocation.taxState ? (
            <span>
              {" · "}
              <Button
                type="button"
                color="brand"
                size="xs"
                onClick={() => onProductionStateClick(displayLocation.taxState as ProductionState)}
              >
                View tax incentives
              </Button>
            </span>
          ) : null}
        </>
      ),
    });
  }

  if (hasTaxIncentives) {
    assumptionRows.push({
      id: "tax-incentives",
      label: "Tax incentives",
      value: (
        <span className="flex flex-wrap gap-x-1 gap-y-1">
          {budgetDetail?.tax_incentives?.map((taxIncentive, index) => (
            <span key={`tax_incentive_${index}`}>
              <Button
                type="button"
                color="brand"
                size="xs"
                onClick={() => onTaxIncentiveClick(taxIncentive)}
              >
                {taxIncentive.location_name}
              </Button>
              {index < (budgetDetail?.tax_incentives?.length ?? 0) - 1 ? (
                <span className="text-muted-foreground/60">,</span>
              ) : null}
            </span>
          ))}
        </span>
      ),
    });
  }

  visibleAssumptions.forEach((assume, index) => {
    if (!assume.name && !assume.value) return;
    assumptionRows.push({
      id: `assumption-${index}`,
      label: assume.name ?? "Assumption",
      value: assume.value ?? "—",
    });
  });

  return assumptionRows.length > 0 ? (
    <AssumptionPanel rows={assumptionRows} />
  ) : (
    <AssumptionsEmptyState />
  );
}

function useTaxIncentivesModalState() {
  const [selectedProductionState, setSelectedProductionState] = useState<ProductionState>();
  const [selectedTaxIncentives, setSelectedTaxIncentives] = useState<TaxIncentive>();

  const modal =
    selectedProductionState || selectedTaxIncentives ? (
      <TaxIncentivesModal
        open={!!selectedProductionState || !!selectedTaxIncentives}
        setOpen={() => {
          setSelectedProductionState(undefined);
          setSelectedTaxIncentives(undefined);
        }}
        productionState={selectedProductionState}
        taxIncentive={selectedTaxIncentives}
      />
    ) : null;

  return {
    modal,
    onProductionStateClick: setSelectedProductionState,
    onTaxIncentiveClick: setSelectedTaxIncentives,
  };
}

export const BudgetDetailsDialog: React.FC<BudgetDialogProps> = ({
  budgetDetail,
  open: controlledOpen,
  onOpenChange,
  showTrigger = true,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = (nextOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
    } else {
      setInternalOpen(nextOpen);
    }
  };

  return (
    <>
      {showTrigger ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghostSecondary"
              size="sm"
              aria-label="Budget details"
              onClick={() => setOpen(true)}
            >
              <Info />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Budget details</TooltipContent>
        </Tooltip>
      ) : null}

      <Modal isOpen={isOpen} onClose={() => setOpen(false)} maxWidth="max-w-lg">
        <ModalHeader title="Budget details" onClose={() => setOpen(false)} />
        <ModalBody className="text-foreground/90">
          <BudgetDetailsContent budgetDetail={budgetDetail} />
        </ModalBody>
      </Modal>
    </>
  );
};

export const BudgetAssumptionsDialog: React.FC<BudgetDialogProps> = ({
  budgetDetail,
  open: controlledOpen,
  onOpenChange,
  showTrigger = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = (nextOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
    } else {
      setInternalOpen(nextOpen);
    }
  };

  const { displayLocation, showProductionBase, showTaxIncentiveLink } =
    useBudgetSummaryData(budgetDetail);
  const { modal, onProductionStateClick, onTaxIncentiveClick } = useTaxIncentivesModalState();

  return (
    <>
      {showTrigger ? (
        <Button variant="ghostSecondary" size="sm" onClick={() => setOpen(true)}>
          Assumptions
        </Button>
      ) : null}

      <Modal isOpen={isOpen} onClose={() => setOpen(false)} maxWidth="max-w-lg">
        <ModalHeader title="Budget assumptions" onClose={() => setOpen(false)} />
        <ModalBody className="text-foreground/90">
          <BudgetAssumptionsContent
            budgetDetail={budgetDetail}
            displayLocation={displayLocation}
            showProductionBase={showProductionBase}
            showTaxIncentiveLink={showTaxIncentiveLink}
            onProductionStateClick={onProductionStateClick}
            onTaxIncentiveClick={onTaxIncentiveClick}
          />
        </ModalBody>
      </Modal>

      {modal}
    </>
  );
};

export const BudgetSummary: React.FC<BudgetDialogProps & { variant?: "inline" | "dialog" }> = ({
  budgetDetail,
  variant = "dialog",
  open,
  onOpenChange,
  showTrigger = true,
}) => {
  const { displayLocation, showProductionBase, showTaxIncentiveLink } =
    useBudgetSummaryData(budgetDetail);
  const { modal, onProductionStateClick, onTaxIncentiveClick } = useTaxIncentivesModalState();

  if (variant === "inline") {
    return (
      <>
        <div className={`${BUDGET_PANEL_CARD_CLASS} mb-4 flex flex-col flex-none gap-5 px-5 py-4`}>
          <BudgetDetailsContent budgetDetail={budgetDetail} />
          <div className="flex flex-col gap-2.5">
            <SectionLabel>Assumptions</SectionLabel>
            <BudgetAssumptionsContent
              budgetDetail={budgetDetail}
              displayLocation={displayLocation}
              showProductionBase={showProductionBase}
              showTaxIncentiveLink={showTaxIncentiveLink}
              onProductionStateClick={onProductionStateClick}
              onTaxIncentiveClick={onTaxIncentiveClick}
            />
          </div>
        </div>
        {modal}
      </>
    );
  }

  return (
    <>
      <BudgetDetailsDialog
        budgetDetail={budgetDetail}
        open={open}
        onOpenChange={onOpenChange}
        showTrigger={showTrigger}
      />
      {modal}
    </>
  );
};
